import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeColor } from '@view-model/models/common/color';
import { NodeStyle } from '@view-model/models/sticky/StickyNodeView';

// ドラッグとクリックを区別するための移動距離閾値（px）
const DRAG_THRESHOLD = 5;

type ClientPoint = { x: number; y: number };

type Props = {
  color: ThemeColor;
  onClick: (color: ThemeColor) => void;
  onDrag: (clientPoint: ClientPoint, color: ThemeColor) => void;
  onDragEnd: (clientPoint: ClientPoint, color: ThemeColor) => void;
  onDragCancel: () => void;
};

export const NodeColorSwatch: React.FC<Props> = ({ color, onClick, onDrag, onDragEnd, onDragCancel }: Props) => {
  const { t } = useTranslation();
  const isDraggingRef = useRef(false);
  const { fillColor, borderColor } = NodeStyle.getStyles(color);

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    isDraggingRef.current = false;

    const cleanup = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleWindowBlur);
      document.body.style.cursor = '';
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (moveEvent.buttons === 0) {
        const wasDragging = isDraggingRef.current;
        isDraggingRef.current = false;
        cleanup();
        if (wasDragging) {
          onDragCancel();
        }
        return;
      }

      if (!isDraggingRef.current) {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        if (dx * dx + dy * dy >= DRAG_THRESHOLD * DRAG_THRESHOLD) {
          isDraggingRef.current = true;
          document.body.style.cursor = 'grabbing';
        }
      }
      if (isDraggingRef.current) {
        onDrag({ x: moveEvent.clientX, y: moveEvent.clientY }, color);
      }
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      const wasDragging = isDraggingRef.current;
      isDraggingRef.current = false;
      cleanup();

      if (wasDragging) {
        const isOutsideViewport =
          upEvent.clientX < 0 ||
          upEvent.clientX > window.innerWidth ||
          upEvent.clientY < 0 ||
          upEvent.clientY > window.innerHeight;
        if (isOutsideViewport) {
          onDragCancel();
        } else {
          onDragEnd({ x: upEvent.clientX, y: upEvent.clientY }, color);
        }
      } else {
        onClick(color);
      }
    };

    const handleWindowBlur = () => {
      const wasDragging = isDraggingRef.current;
      isDraggingRef.current = false;
      cleanup();
      if (wasDragging) {
        onDragCancel();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleWindowBlur);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(color);
    }
  };

  return (
    <button
      type="button"
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      className="size-8 cursor-grab rounded transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-1"
      style={{
        backgroundColor: fillColor,
        border: `2px solid ${borderColor}`,
      }}
      aria-label={t(ThemeColor.toColorNameKey(color))}
    />
  );
};
